import React, { useCallback, useEffect, useRef, useState } from "react";
import apiUrl from "../Apiurl";
import { TextSkeleton } from "../interfaccia/loading";

// Freschezza delle repliche usate dalle tab Prezzi (listini) e Tag Moda dei
// filtri avanzati, con aggiornamento a richiesta. Backend:
// GET/POST /moda/articoli/filtri-dati/:tipo/(stato|aggiorna), che avviano lo
// stesso job del monitor processi (lock e stato DAM_Status condivisi).
export const FILTRI_DATI_POLL_MS = 3000;
export const FILTRI_DATI_POLL_MAX = 200; // ~10 minuti, compresi i tentativi falliti
export const FILTRI_DATI_POLL_ERRORI_MAX = 5;

const formatDataOra = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const opzioni = { timeZone: "Europe/Rome" };
  const giorno = date.toLocaleDateString("it-IT", { ...opzioni, day: "2-digit", month: "2-digit", year: "numeric" });
  const ora = date.toLocaleTimeString("it-IT", { ...opzioni, hour: "2-digit", minute: "2-digit" });
  return `${giorno} ${ora}`;
};

const isCancel = (error) => error?.name === "AbortError";

// fetch e non axios: il modale dei filtri non deve dipendere da axios.
async function richiestaJson(url, { method = "GET", token, body, signal } = {}) {
  const response = await fetch(url, {
    method,
    signal,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

export default function FiltriDatiAggiornamento({ tipo, etichetta, token, username, onAggiornato }) {
  const [stato, setStato] = useState(null);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState("");
  const [esito, setEsito] = useState(null); // { tipo: "in-corso" | "ok" | "errore" | "avviso", testo }
  const [daRicontrollare, setDaRicontrollare] = useState(false);
  const [ricontrollo, setRicontrollo] = useState(false);
  const controllerRef = useRef(null);
  const pollRef = useRef(null);
  const precedenteRef = useRef(null);
  const cicloRef = useRef(0);

  const leggiStato = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const payload = await richiestaJson(`${apiUrl}/moda/articoli/filtri-dati/${tipo}/stato`, {
      token,
      signal: controller.signal,
    });
    if (controller.signal.aborted) throw new DOMException("Lettura annullata", "AbortError");
    return payload?.data ?? null;
  }, [tipo, token]);

  const fermaPolling = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = null;
  };

  useEffect(() => {
    let attivo = true;
    setCaricamento(true);
    setErrore("");
    leggiStato()
      .then((data) => {
        if (!attivo) return;
        setStato(data);
        if (data?.inCorso) avviaPolling(data, 0);
      })
      .catch((error) => {
        if (attivo && !isCancel(error)) setErrore("Stato dei dati non disponibile.");
      })
      .finally(() => {
        if (attivo) setCaricamento(false);
      });
    return () => {
      attivo = false;
      cicloRef.current += 1;
      controllerRef.current?.abort();
      fermaPolling();
    };
    // avviaPolling usa solo ref e setter stabili
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leggiStato]);

  function applicaEsito(data, precedente) {
    setDaRicontrollare(false);
    const riuscito =
      data?.ultimaEsecuzione?.status === "OK" && data?.aggiornatoIl && data.aggiornatoIl !== precedente?.aggiornatoIl;
    if (riuscito) {
      setEsito({ tipo: "ok", testo: "Aggiornamento completato." });
      onAggiornato?.(data);
    } else {
      setEsito({
        tipo: "errore",
        testo: data?.ultimaEsecuzione?.messaggio || "Aggiornamento non riuscito.",
      });
    }
  }

  function richiediRicontrollo(testo) {
    fermaPolling();
    setDaRicontrollare(true);
    setEsito({ tipo: "avviso", testo });
  }

  function avviaPolling(precedente, tentativo, erroriConsecutivi = 0) {
    const ciclo = cicloRef.current;
    precedenteRef.current = precedente;
    setDaRicontrollare(false);
    setEsito({
      tipo: "in-corso",
      testo: erroriConsecutivi ? "Stato non raggiungibile, nuovo tentativo…" : "Aggiornamento in corso…",
    });
    fermaPolling();
    pollRef.current = setTimeout(async () => {
      pollRef.current = null;
      try {
        const data = await leggiStato();
        if (ciclo !== cicloRef.current) return;
        setStato(data);
        if (data?.inCorso && tentativo + 1 < FILTRI_DATI_POLL_MAX) {
          avviaPolling(precedente, tentativo + 1);
          return;
        }
        if (data?.inCorso) {
          richiediRicontrollo("Aggiornamento ancora in corso: ricontrolla lo stato.");
          return;
        }
        applicaEsito(data, precedente);
      } catch (error) {
        if (ciclo !== cicloRef.current || isCancel(error)) return;
        if (erroriConsecutivi + 1 >= FILTRI_DATI_POLL_ERRORI_MAX || tentativo + 1 >= FILTRI_DATI_POLL_MAX) {
          richiediRicontrollo("Verifica dello stato sospesa: ricontrolla lo stato dell'aggiornamento.");
          return;
        }
        avviaPolling(precedente, tentativo + 1, erroriConsecutivi + 1);
      }
    }, FILTRI_DATI_POLL_MS);
  }

  const ricontrollaStato = async () => {
    const ciclo = cicloRef.current;
    setRicontrollo(true);
    try {
      const data = await leggiStato();
      if (ciclo !== cicloRef.current) return;
      setStato(data);
      if (data?.inCorso) avviaPolling(precedenteRef.current, 0);
      else applicaEsito(data, precedenteRef.current);
    } catch (error) {
      if (ciclo === cicloRef.current && !isCancel(error)) {
        richiediRicontrollo("Stato non raggiungibile: ricontrolla più tardi.");
      }
    } finally {
      if (ciclo === cicloRef.current) setRicontrollo(false);
    }
  };

  const aggiorna = async () => {
    const ciclo = cicloRef.current;
    const precedente = stato;
    setEsito({ tipo: "in-corso", testo: "Avvio aggiornamento…" });
    try {
      await richiestaJson(`${apiUrl}/moda/articoli/filtri-dati/${tipo}/aggiorna`, {
        method: "POST",
        token,
        body: { username },
      });
      if (ciclo !== cicloRef.current) return;
      setStato((prev) => (prev ? { ...prev, inCorso: true } : prev));
      avviaPolling(precedente, 0);
    } catch (error) {
      if (ciclo !== cicloRef.current || isCancel(error)) return;
      const status = error?.status;
      if (status === 409) {
        // Già in esecuzione (anche dal mirror notturno o da un collega): si segue quella.
        setStato((prev) => (prev ? { ...prev, inCorso: true } : prev));
        avviaPolling(precedente, 0);
        return;
      }
      setEsito({
        tipo: "errore",
        testo: error?.payload?.error || "Impossibile avviare l'aggiornamento.",
      });
    }
  };

  if (caricamento) {
    return (
      <div className="mb3" aria-busy="true">
        <TextSkeleton lines={1} lineHeight={14} width="70%" />
      </div>
    );
  }

  const dataOra = formatDataOra(stato?.aggiornatoIl);
  const inCorso = !daRicontrollare && (Boolean(stato?.inCorso) || esito?.tipo === "in-corso");
  const motivoDisabilitato = !stato?.canRun && !inCorso
    ? "Serve il permesso update:database per aggiornare i dati."
    : !username
      ? "Username Domino non disponibile: aggiorna la sessione."
      : null;

  return (
    <div className="mb3 pa2 br2 bg-near-white f7 lh-copy flex items-center justify-between flex-wrap">
      <div className="mr2">
        {errore ? (
          <span className="dark-red">{errore}</span>
        ) : (
          <span>
            {etichetta} {dataOra ?? "data non disponibile"}
          </span>
        )}
        {motivoDisabilitato && !errore && <div className="gray">{motivoDisabilitato}</div>}
        {esito && (
          <div
            role={esito.tipo === "errore" ? "alert" : "status"}
            className={esito.tipo === "ok" ? "dark-green" : esito.tipo === "errore" ? "dark-red" : "gray"}
          >
            {esito.testo}
          </div>
        )}
      </div>
      {daRicontrollare && (
        <button
          type="button"
          className="button-reset ba br2 pointer pv1 ph2 f7 sfondoBiancoTema"
          onClick={ricontrollaStato}
          disabled={ricontrollo}
        >
          Ricontrolla stato
        </button>
      )}
      <button
        type="button"
        className="button-reset ba br2 pointer pv1 ph2 f7 sfondoBiancoTema"
        onClick={aggiorna}
        disabled={inCorso || daRicontrollare || Boolean(motivoDisabilitato) || Boolean(errore)}
      >
        {inCorso ? "Aggiornamento…" : "Aggiorna"}
      </button>
    </div>
  );
}
